import type { ReactNode } from 'react';


import { cn } from '@/lib/utils';

import {
  EmptyAlertsIllustration,
  EmptyCatalogIllustration,
  EmptyDashboardIllustration,
  EmptyForecastsIllustration,
  EmptyGenericIllustration,
  EmptyPromptIllustration,
  type IllustrationKind,
} from './illustrations/index.js';
import { Card, CardContent } from './ui/card.js';
import { Skeleton } from './ui/skeleton.js';


/**
 * State-Panels fuer Loading / Error / Empty.
 *
 * Wird ueberall in den Pages und im WidgetRenderer verwendet. API
 * (Komponentennamen + Props) ist bewusst stabil — Theme-Polish darf nicht
 * Page-Code brechen. Neu in MR C: `EmptyPanel` akzeptiert optional eine
 * `illustration`-Prop fuer brand-getreue SVG-Illustrationen.
 */

export function LoadingPanel({ label = 'Laden...' }: { label?: string }): JSX.Element {
  return (
    <Card
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="animate-in fade-in border-dashed bg-muted/30 duration-base"
    >
      <CardContent className="flex flex-col gap-3 p-6">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary/60" />
          </span>
          <span>{label}</span>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      </CardContent>
    </Card>
  );
}

export function ErrorPanel({
  title,
  message,
}: {
  title: string;
  message: string;
}): JSX.Element {
  return (
    <Card
      role="alert"
      className="animate-in fade-in slide-in-from-bottom-1 border-destructive/60 bg-destructive/5 duration-base"
    >
      <CardContent className="p-4">
        <div className="font-medium text-destructive">{title}</div>
        <div className="mt-1 text-sm text-destructive/90">{message}</div>
      </CardContent>
    </Card>
  );
}

interface EmptyPanelProps {
  children: ReactNode;
  /**
   * Optionale Brand-Illustration ueber dem Text. Stil: stroke-only, 96-128 px,
   * currentColor + text-brand-500-Akzente. Ohne Angabe wird
   * die neutrale Illustration gezeigt, damit alle leeren Seiten gleich aussehen; `false` laesst
   * sie weg (z. B. in kleinen Karten).
   */
  illustration?: IllustrationKind | false;
  /** Optional: zusaetzliche Klassen am CardContent. */
  className?: string;
}

const ILLUSTRATION_MAP: Record<IllustrationKind, (props: { className?: string }) => JSX.Element> = {
  dashboard: EmptyDashboardIllustration,
  catalog: EmptyCatalogIllustration,
  prompt: EmptyPromptIllustration,
  alerts: EmptyAlertsIllustration,
  forecasts: EmptyForecastsIllustration,
  generic: EmptyGenericIllustration,
};

export function EmptyPanel({ children, illustration, className }: EmptyPanelProps): JSX.Element {
  const Illustration = illustration === false ? null : ILLUSTRATION_MAP[illustration ?? 'generic'];
  return (
    <Card className="animate-in fade-in border-dashed border-border bg-card shadow-none duration-base">
      <CardContent
        className={cn(
          'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center text-sm text-muted-foreground',
          className,
        )}
      >
        {Illustration ? (
          <Illustration className="h-16 w-16 text-muted-foreground/60" />
        ) : null}
        <div className="max-w-md leading-relaxed">{children}</div>
      </CardContent>
    </Card>
  );
}
